import { useRef, useState } from "react";
import { PitchShape, PitchLines } from "./PitchShape";
import { ZoomIn, Maximize2 } from "lucide-react";
import { EQUIPMENT_IDS, DRAG_TOOLS } from "../../lib/tactical/constants";

/**
 * Interactive tactical canvas. Owns: drawing in progress, pinch-zoom, pointer events.
 * Lifts state via callbacks: onAddPlayer, onMovePlayer, onRemovePlayer, onAddDrawing.
 */
export default function TacticalCanvas({
  frame,
  tool,
  color,
  isPlaying,
  segmentIdx,
  framesTotal,
  onMovePlayer,
  onRemovePlayer,
  onRenamePlayer,
  onAddDrawing,
  onAddText,
  onAddGoal,
  onRemoveDrawing,
}) {
  const svgRef = useRef(null);
  const [draggedId, setDraggedId] = useState(null);
  const [drawing, setDrawing] = useState(null);
  const [zoom, setZoom] = useState({ scale: 1, x: 0, y: 0 });
  const touchStateRef = useRef({
    mode: null, startDist: 0, startScale: 1,
    start: { x: 0, y: 0 }, startTransform: { x: 0, y: 0 },
  });

  const getSvgPoint = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    return { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
  };

  const onPointerDown = (e) => {
    if (isPlaying) return;
    const pt = getSvgPoint(e);
    if (tool === "text") {
      const text = window.prompt("Testo da inserire:");
      if (text && text.trim()) onAddText({ x: pt.x, y: pt.y, text: text.trim(), color });
      return;
    }
    if (tool === "goal") {
      onAddGoal({ x: pt.x, y: pt.y, rotation: pt.y > 50 ? 0 : 180, color: "#ffffff" });
      return;
    }
    if (tool === "line" || tool === "arrow") {
      setDrawing({ type: tool, points: [pt, pt], color });
    } else if (tool === "free") {
      setDrawing({ type: "free", points: [pt], color });
    }
  };

  const onPointerMove = (e) => {
    if (isPlaying) return;
    if (draggedId !== null && tool === "select") {
      const pt = getSvgPoint(e);
      onMovePlayer(draggedId, pt);
      return;
    }
    if (!drawing) return;
    const pt = getSvgPoint(e);
    if (drawing.type === "free") {
      setDrawing({ ...drawing, points: [...drawing.points, pt] });
    } else {
      setDrawing({ ...drawing, points: [drawing.points[0], pt] });
    }
  };

  const onPointerUp = () => {
    if (draggedId !== null) setDraggedId(null);
    if (drawing) {
      onAddDrawing(drawing);
      setDrawing(null);
    }
  };

  const startDrag = (e, id) => {
    if (isPlaying) return;
    if (tool === "erase") {
      e.stopPropagation();
      onRemovePlayer(id);
      return;
    }
    if (tool !== "select") return;
    e.stopPropagation();
    setDraggedId(id);
  };

  const onPlayerDoubleClick = (e, player) => {
    if (isPlaying) return;
    e.stopPropagation();
    const current = player.name || player.label || "";
    const next = window.prompt("Nome giocatore (vuoto = solo numero):", current);
    if (next === null) return; // user cancelled
    onRenamePlayer && onRenamePlayer(player.id, next.trim());
  };

  const onDrawingClick = (e, idx) => {
    if (isPlaying) return;
    if (tool !== "erase") return;
    e.stopPropagation();
    onRemoveDrawing && onRemoveDrawing(idx);
  };

  // Touch handlers for pinch zoom + pan
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      setDrawing(null);
      setDraggedId(null);
      const [t1, t2] = e.touches;
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      touchStateRef.current = {
        mode: "pinch", startDist: dist, startScale: zoom.scale,
        start: { x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 },
        startTransform: { x: zoom.x, y: zoom.y },
      };
    } else if (e.touches.length === 1 && zoom.scale > 1.05) {
      setDrawing(null);
      setDraggedId(null);
      const t = e.touches[0];
      touchStateRef.current = {
        mode: "pan", startDist: 0, startScale: zoom.scale,
        start: { x: t.clientX, y: t.clientY },
        startTransform: { x: zoom.x, y: zoom.y },
      };
    }
  };
  const onTouchMove = (e) => {
    const st = touchStateRef.current;
    if (st.mode === "pinch" && e.touches.length === 2) {
      const [t1, t2] = e.touches;
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      const ratio = dist / (st.startDist || 1);
      const newScale = Math.max(1, Math.min(4, st.startScale * ratio));
      setZoom((z) => ({ ...z, scale: newScale }));
    } else if (st.mode === "pan" && e.touches.length === 1) {
      const t = e.touches[0];
      setZoom((z) => ({
        ...z,
        x: st.startTransform.x + (t.clientX - st.start.x),
        y: st.startTransform.y + (t.clientY - st.start.y),
      }));
    }
  };
  const onTouchEnd = () => {
    touchStateRef.current = { mode: null, startDist: 0, startScale: 1, start: { x: 0, y: 0 }, startTransform: { x: 0, y: 0 } };
  };
  const resetZoom = () => setZoom({ scale: 1, x: 0, y: 0 });
  const zoomedIn = zoom.scale > 1.01 || zoom.x !== 0 || zoom.y !== 0;

  const arrowColors = [
    ...new Set(
      [...frame.drawings, drawing]
        .filter(Boolean)
        .filter((d) => ["arrow", "run_arrow", "dribble_arrow"].includes(d.type))
        .map((d) => d.color),
    ),
  ];

  const cursorClass =
    isPlaying ? "cursor-default"
      : tool === "erase" ? "cursor-not-allowed"
      : tool === "text" ? "cursor-text"
      : tool === "select" ? "cursor-grab"
      : "cursor-crosshair";

  return (
    <div
      className="rounded-2xl overflow-hidden pitch-bg relative aspect-[3/4] sm:aspect-[4/3] shadow-lg ring-1 ring-emerald-900/20"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      data-testid="pitch-container"
    >
      <svg
        ref={svgRef}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className={`w-full h-full touch-none select-none ${cursorClass}`}
        style={{
          transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`,
          transformOrigin: "0 0",
          transition: touchStateRef.current.mode ? "none" : "transform 0.2s ease",
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        data-testid="pitch-svg"
      >
        <defs>
          {arrowColors.map((c) => (
            <marker
              key={c}
              id={`arrow-${c.replace("#", "")}`}
              viewBox="0 0 10 10"
              refX="6" refY="5"
              markerWidth="4" markerHeight="4"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill={c} />
            </marker>
          ))}
        </defs>
        <PitchLines />
        {frame.drawings.map((d, i) => (
          <g key={`d-${i}`} onPointerDown={(e) => onDrawingClick(e, i)} style={{ cursor: tool === "erase" && !isPlaying ? "not-allowed" : "default" }}>
            <PitchShape k={`d-${i}`} shape={d} />
          </g>
        ))}
        {drawing && <PitchShape key="live" k="live" shape={drawing} />}
        {frame.players.map((p) => {
          const hasName = p.name && p.name.trim();
          return (
            <g
              key={p.id}
              transform={`translate(${p.x}, ${p.y})`}
              onPointerDown={(e) => startDrag(e, p.id)}
              onDoubleClick={(e) => onPlayerDoubleClick(e, p)}
              className="tactical-player"
              style={{ cursor: tool === "select" && !isPlaying ? "grab" : "default" }}
            >
              <circle r="3.6" fill="rgba(0,0,0,0.18)" />
              <circle r="3.2" fill={p.color} stroke="white" strokeWidth="0.4" />
              <text
                textAnchor="middle"
                dominantBaseline="central"
                fontSize="2.5"
                fontWeight="700"
                fill="white"
                style={{ pointerEvents: "none", userSelect: "none" }}
              >
                {p.label}
              </text>
              {hasName && (
                <g style={{ pointerEvents: "none", userSelect: "none" }}>
                  <rect
                    x="-5.5"
                    y="3.8"
                    width="11"
                    height="2.4"
                    rx="0.5"
                    fill="rgba(0,0,0,0.75)"
                  />
                  <text
                    x="0"
                    y="5"
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize="1.55"
                    fontWeight="600"
                    fill="white"
                  >
                    {p.name.length > 14 ? `${p.name.slice(0, 13)}…` : p.name}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>

      {/* Playback status overlay */}
      {isPlaying && (
        <div className="absolute top-3 left-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md text-white text-xs font-mono shadow-lg">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          PLAY · Frame {segmentIdx + 1} / {framesTotal - 1}
        </div>
      )}

      {/* Mobile zoom helper hint + reset button */}
      {zoomedIn ? (
        <button
          onClick={resetZoom}
          className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm border border-slate-200 text-xs font-semibold shadow-sm hover:bg-white"
          data-testid="reset-zoom-btn"
        >
          <Maximize2 size={13} /> Reset zoom · {zoom.scale.toFixed(1)}x
        </button>
      ) : (
        <div className="md:hidden absolute bottom-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/40 text-white text-[10px] backdrop-blur-sm pointer-events-none">
          <ZoomIn size={11} /> Pizzica per zoomare
        </div>
      )}
    </div>
  );
}
